import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchMarketBoard,
  marketDataConfig,
  type MarketQuoteItem,
} from '../../../services/marketDataApi';

type MarketKey = 'gold' | 'silver';

type MarketDefinition = {
  key: MarketKey;
  name: string;
  providerSymbol: string;
  icon: string;
};

const GOLD_MARKET: MarketDefinition = {
  key: 'gold',
  name: 'Gold',
  providerSymbol: 'GCUSD',
  icon: 'ri-copper-coin-line',
};

const SILVER_MARKET: MarketDefinition = {
  key: 'silver',
  name: 'Silver',
  providerSymbol: 'SIUSD',
  icon: 'ri-copper-diamond-line',
};

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const quotePrice = (quote?: MarketQuoteItem): number | null =>
  finiteNumber(quote?.price) ?? finiteNumber(quote?.sellingPrice) ?? finiteNumber(quote?.buyingPrice);

const formatPrice = (value: unknown, scale = 2) => {
  const numeric = finiteNumber(value);
  if (numeric === null) return '—';

  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: Math.min(Math.max(scale, 0), 5),
    maximumFractionDigits: Math.min(Math.max(scale, 0), 5),
  }).format(numeric);
};

const formatSigned = (value: unknown, scale = 2) => {
  const numeric = finiteNumber(value);
  if (numeric === null) return '—';
  return `${numeric > 0 ? '+' : ''}${formatPrice(numeric, scale)}`;
};

const formatPercent = (value: unknown) => {
  const numeric = finiteNumber(value);
  if (numeric === null) return '—';
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(2)}%`;
};

const formatHongKongDateTime = (value?: string | null) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: marketDataConfig.timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const findQuote = (items: MarketQuoteItem[], definition: MarketDefinition) =>
  items.find((item) => {
    const providerSymbol = String(item.providerSymbol || '').toUpperCase();
    const displayName = String(item.displayName || '').toLowerCase();
    const displaySymbol = String(item.displaySymbol || '').toLowerCase();
    const expectedName = definition.name.toLowerCase();

    return (
      providerSymbol === definition.providerSymbol ||
      displayName === expectedName ||
      displaySymbol === expectedName ||
      displayName.includes(expectedName) ||
      displaySymbol.includes(expectedName)
    );
  });

type MarketPriceCardProps = {
  definition: MarketDefinition;
  quote?: MarketQuoteItem;
  loading: boolean;
  lastRefresh: Date | null;
  localizedName: string;
};

function MarketPriceCard({
  definition,
  quote,
  loading,
  lastRefresh,
  localizedName,
}: MarketPriceCardProps) {
  const { t } = useTranslation();
  const isGold = definition.key === 'gold';
  const priceScale = Math.min(Math.max(Number(quote?.priceScale) || 2, 0), 5);
  const price = quotePrice(quote);
  const change = finiteNumber(quote?.change);
  const changePercentage = finiteNumber(quote?.changePercentage);
  const movement = changePercentage ?? change ?? 0;
  const isPositive = movement >= 0;
  const symbol = quote?.providerSymbol || definition.providerSymbol;
  const updateTime = quote?.fetchedAt || quote?.providerTimestamp || lastRefresh?.toISOString() || null;

  return (
    <article
      className={`group relative overflow-hidden rounded-[28px] border p-6 sm:p-8 md:p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
        isGold
          ? 'border-yellow-400/25 bg-gradient-to-br from-yellow-400/[0.10] via-white/[0.055] to-white/[0.025] hover:border-yellow-400/45 hover:shadow-yellow-500/10'
          : 'border-slate-300/20 bg-gradient-to-br from-slate-200/[0.10] via-white/[0.055] to-white/[0.025] hover:border-slate-200/40 hover:shadow-slate-300/10'
      }`}
    >
      <div
        className={`pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl ${
          isGold ? 'bg-yellow-400/10' : 'bg-slate-300/10'
        }`}
      />

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border ${
                isGold
                  ? 'border-yellow-400/35 bg-yellow-400/15 text-yellow-300'
                  : 'border-slate-300/30 bg-slate-300/10 text-slate-200'
              }`}
            >
              <i className={`${definition.icon} text-2xl`} />
            </div>

            <div className="min-w-0">
              <h3 className="truncate text-2xl font-bold text-white sm:text-3xl">{localizedName}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-400">
                <span>{symbol}</span>
                <span className="text-gray-600">•</span>
                <span>USD</span>
              </div>
            </div>
          </div>

          <div
            className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] ${
              quote?.stale || quote?.delayed
                ? 'border-amber-400/25 bg-amber-400/10 text-amber-300'
                : 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                quote?.stale || quote?.delayed ? 'bg-amber-300' : 'bg-emerald-300 animate-pulse'
              }`}
            />
            {quote?.stale || quote?.delayed
              ? t('markets.status.delayed', { defaultValue: 'Delayed' })
              : t('markets.status.live', { defaultValue: 'Live' })}
          </div>
        </div>

        <div className="mt-9 sm:mt-10">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            {t('markets.livePrice', { defaultValue: 'Live Price' })}
          </div>
          <div
            className={`mt-2 break-all font-mono text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl ${
              isGold ? 'text-yellow-300' : 'text-slate-100'
            } ${loading ? 'animate-pulse' : ''}`}
          >
            {price === null ? '—' : `$${formatPrice(price, priceScale)}`}
          </div>

          {(change !== null || changePercentage !== null) && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex items-center gap-1 text-lg font-semibold sm:text-xl ${
                  isPositive ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                <i className={isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                {formatSigned(change, priceScale)}
              </span>
              <span
                className={`rounded-lg border px-2.5 py-1 text-sm font-bold ${
                  isPositive
                    ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300'
                    : 'border-red-400/25 bg-red-400/10 text-red-300'
                }`}
              >
                {formatPercent(changePercentage)}
              </span>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4">
          <div className="rounded-2xl border border-white/10 bg-black/15 p-4 sm:p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500">
              {t('markets.sell', { defaultValue: 'Sell' })}
            </div>
            <div className="mt-2 font-mono text-lg font-semibold text-white sm:text-xl">
              {finiteNumber(quote?.sellingPrice) === null
                ? '—'
                : `$${formatPrice(quote?.sellingPrice, priceScale)}`}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-4 sm:p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500">
              {t('markets.buy', { defaultValue: 'Buy' })}
            </div>
            <div className="mt-2 font-mono text-lg font-semibold text-white sm:text-xl">
              {finiteNumber(quote?.buyingPrice) === null
                ? '—'
                : `$${formatPrice(quote?.buyingPrice, priceScale)}`}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-4 sm:p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500">
              {t('markets.dayHigh', { defaultValue: 'Day High' })}
            </div>
            <div className="mt-2 font-mono text-lg font-semibold text-emerald-300 sm:text-xl">
              {finiteNumber(quote?.dayHigh) === null
                ? '—'
                : `$${formatPrice(quote?.dayHigh, priceScale)}`}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/15 p-4 sm:p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-gray-500">
              {t('markets.dayLow', { defaultValue: 'Day Low' })}
            </div>
            <div className="mt-2 font-mono text-lg font-semibold text-red-300 sm:text-xl">
              {finiteNumber(quote?.dayLow) === null
                ? '—'
                : `$${formatPrice(quote?.dayLow, priceScale)}`}
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5 text-xs text-gray-500">
          <span>
            {t('markets.lastUpdate', { defaultValue: 'Last update' })}: {formatHongKongDateTime(updateTime)}
          </span>
          <span>{t('markets.hongKongTime', { defaultValue: 'Hong Kong time' })}</span>
        </div>
      </div>
    </article>
  );
}

export default function MarketSection() {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState<Record<MarketKey, MarketQuoteItem | undefined>>({
    gold: undefined,
    silver: undefined,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    let activeController: AbortController | null = null;

    const loadBoard = async (showLoading: boolean) => {
      if (document.visibilityState === 'hidden') return;

      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      if (showLoading) setLoading(true);

      try {
        const response = await fetchMarketBoard('COMMODITY', controller.signal);

        setQuotes({
          gold: findQuote(response.items, GOLD_MARKET),
          silver: findQuote(response.items, SILVER_MARKET),
        });
        setLastRefresh(new Date());
        setError('');
      } catch (loadError) {
        if (!(loadError instanceof DOMException && loadError.name === 'AbortError')) {
          setError(loadError instanceof Error ? loadError.message : 'Unable to load market prices.');
        }
      } finally {
        if (activeController === controller) {
          setLoading(false);
        }
      }
    };

    void loadBoard(true);

    const intervalId = window.setInterval(
      () => void loadBoard(false),
      marketDataConfig.quoteRefreshMs
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void loadBoard(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activeController?.abort();
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#070912] py-20 md:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[8%] top-10 h-72 w-72 rounded-full bg-yellow-400/[0.035] blur-3xl" />
        <div className="absolute bottom-0 right-[8%] h-80 w-80 rounded-full bg-slate-300/[0.035] blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto mb-12 max-w-3xl text-center md:mb-14">
          <div className="mb-3 text-sm font-semibold uppercase tracking-[0.22em] text-yellow-400">
            {t('markets.preciousMetals', { defaultValue: 'Precious Metals' })}
          </div>
          <h2 className="mb-5 text-4xl font-bold text-white md:text-5xl">{t('markets.title')}</h2>
          <p className="text-lg leading-relaxed text-gray-400 md:text-xl">
            {t('markets.liveSubtitle', {
              defaultValue: 'Follow the latest indicative gold and silver prices in one view.',
            })}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <MarketPriceCard
            definition={GOLD_MARKET}
            quote={quotes.gold}
            loading={loading}
            lastRefresh={lastRefresh}
            localizedName={t('markets.gold')}
          />

          <MarketPriceCard
            definition={SILVER_MARKET}
            quote={quotes.silver}
            loading={loading}
            lastRefresh={lastRefresh}
            localizedName={t('markets.silver')}
          />
        </div>

        {error && (
          <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-red-400/20 bg-red-400/10 px-5 py-4 text-center text-sm text-red-200">
            <i className="ri-error-warning-line mr-2" />
            {t('markets.quotesUnavailable', { defaultValue: error })}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-gray-500">
          {t('markets.indicativeDisclaimer', {
            defaultValue: 'Prices are indicative, may be delayed and are not executable trading prices.',
          })}
        </p>
      </div>
    </section>
  );
}
